import type { FilterSpecification } from "maplibre-gl";
import type { PoiDensityAdapter } from "./index.ts";

const POI_LAYER_ID = "poi-label";
const TRANSIT_LAYER_ID = "public-transport-label";
const AERODROME_MIN_ZOOM = 12;

export const jawgPoiDensityAdapter: PoiDensityAdapter = (
  baselines,
  zoomShift,
  warn,
) => {
  const overrides = [];

  const poiBaseline = baselines.find((layer) => layer.id === POI_LAYER_ID);
  if (!poiBaseline) {
    warn(
      `POI density: Jawg style is missing the expected ${POI_LAYER_ID} layer.`,
    );
  } else {
    const filter = shiftPoiZoomStops(poiBaseline.filter, zoomShift);
    if (filter) {
      overrides.push({ layerId: POI_LAYER_ID, filter });
    } else {
      warn(
        `POI density: Jawg ${POI_LAYER_ID} filter no longer has the expected zoomrank expression.`,
      );
    }
  }

  const transitBaseline = baselines.find((layer) =>
    layer.id === TRANSIT_LAYER_ID
  );
  if (!transitBaseline) {
    warn(
      `POI density: Jawg style is missing the expected ${TRANSIT_LAYER_ID} layer.`,
    );
  } else {
    const filter = shiftTransitRankCutoff(transitBaseline.filter, zoomShift);
    if (filter) {
      overrides.push({ layerId: TRANSIT_LAYER_ID, filter });
    } else {
      warn(
        `POI density: Jawg ${TRANSIT_LAYER_ID} filter no longer has the expected zoomrank expression.`,
      );
    }
  }

  return overrides;
};

function shiftPoiZoomStops(
  baselineFilter: unknown,
  zoomShift: number,
): FilterSpecification | undefined {
  const filter = structuredClone(baselineFilter);
  // Jawg currently wraps the zoomrank condition as the first child of an
  // ["all", ...conditions] expression. Confirm that outer shape before reading
  // filter[1]; otherwise a provider style change could make us edit an
  // unrelated expression at the same array position.
  if (!Array.isArray(filter) || filter[0] !== "all") return;

  const zoomrankCondition = filter[1];
  if (!isZoomrankCondition(zoomrankCondition)) return;
  const step = zoomrankCondition[2];

  // A step expression is ["step", input, defaultOutput, stop, output, ...].
  // Its zoom stops therefore begin at index 3 and repeat at every other index;
  // the intervening values are zoomrank outputs and must remain unchanged.
  for (let index = 3; index < step.length; index += 2) {
    const zoom = step[index];
    if (typeof zoom !== "number") return;
    step[index] = Math.max(0, Math.min(24, zoom + zoomShift));
  }

  // Jawg ranks aerodromes as ordinary transport POIs (some airports are
  // rank 5), which keeps them hidden until z17 even in the densest setting.
  // Its aeroway layers start at z12, so expose airport labels at that
  // same scale and shift the threshold with the density preference.
  if (zoomShift < 0) {
    filter[1] = [
      "any",
      [
        "all",
        [">=", ["zoom"], AERODROME_MIN_ZOOM + zoomShift],
        ["==", ["get", "type"], "aerodrome"],
      ],
      zoomrankCondition,
    ];
  }

  return filter as FilterSpecification;
}

function shiftTransitRankCutoff(
  baselineFilter: unknown,
  zoomShift: number,
): FilterSpecification | undefined {
  const filter = structuredClone(baselineFilter);
  if (!Array.isArray(filter) || filter[0] !== "all") return;

  const rankFallback = filter[1];
  if (
    !Array.isArray(rankFallback) ||
    rankFallback[0] !== "any" ||
    !isZoomrankCutoff(rankFallback[2])
  ) {
    return;
  }

  const cutoff = rankFallback[2][2];
  // Lower zoom stops mean more general POIs. Transit uses the opposite scale:
  // a higher allowed zoomrank includes more labels.
  rankFallback[2][2] = Math.max(0, cutoff - zoomShift);
  return filter as FilterSpecification;
}

// MapLibre expressions are JSON arrays. This deliberately validates Jawg's
// known filter position and shape so provider changes fail visibly.
function isZoomrankCondition(
  value: unknown,
): value is [unknown, unknown, unknown[]] {
  if (!Array.isArray(value) || value[0] !== "<=") return false;
  const property = value[1];
  const step = value[2];
  return Array.isArray(property) &&
    property[0] === "get" &&
    property[1] === "zoomrank" &&
    Array.isArray(step) &&
    step.length >= 5 &&
    step.length % 2 === 1 &&
    step[0] === "step" &&
    Array.isArray(step[1]) &&
    step[1][0] === "zoom";
}

function isZoomrankCutoff(
  value: unknown,
): value is [unknown, unknown, number] {
  if (!Array.isArray(value) || value[0] !== "<=") return false;
  const property = value[1];
  return Array.isArray(property) &&
    property[0] === "get" &&
    property[1] === "zoomrank" &&
    typeof value[2] === "number";
}
